// FeedbackModal.js
import React, { useState, useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { submitFeedback } from '../../services/api'; // Ensure this API function exists

// Full-screen backdrop that dims the background.
const ModalBackdrop = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
});

// Modal "card" container with ample padding and scrollability if needed.
const ModalCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  width: '90%',
  maxWidth: 800,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: theme.shadows[5],
}));

// A custom multi-line text area component.
const CustomTextArea = styled(({ label, error, helperText, ...props }) => (
  <Box sx={{ mb: 2 }}>
    {label && (
      <Typography
        component="label"
        variant="subtitle1"
        sx={{ mb: 1 }}
        htmlFor="feedback-input"
      >
        {label}
      </Typography>
    )}
    <textarea
      {...props}
      id="feedback-input"
      aria-label="Feedback text input"
      aria-invalid={error ? 'true' : 'false'}
      aria-describedby={error ? 'feedback-error' : undefined}
      style={{
        width: '100%',
        padding: '12px',
        fontSize: '1rem',
        fontFamily: 'inherit',
        border: error ? '1px solid #d32f2f' : '1px solid #ccc',
        borderRadius: '4px',
        resize: 'vertical',
        minHeight: '150px',
        lineHeight: 1.5,
        boxSizing: 'border-box',
      }}
    />
    {error && helperText && (
      <Typography
        id="feedback-error"
        variant="caption"
        color="error"
        sx={{ mt: 0.5 }}
        role="alert"
      >
        {helperText}
      </Typography>
    )}
  </Box>
))(({ theme }) => ({}));

function FeedbackModal({ open, onClose }) {
  const [feedback, setFeedback] = useState('');
  const [feedbackError, setFeedbackError] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const characterLimit = 500;

  // Clear the form and reset submission state when the modal opens.
  useEffect(() => {
    if (open) {
      setFeedback('');
      setFeedbackError('');
      setIsSubmitted(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setFeedbackError('Feedback cannot be empty.');
      return;
    }
    if (feedback.length > characterLimit) {
      setFeedbackError(`Feedback cannot exceed ${characterLimit} characters.`);
      return;
    }
    try {
      // Call the API to submit feedback.
      await submitFeedback(feedback);
      // Set the submitted state to true to display the thank-you message.
      setIsSubmitted(true);
    } catch (error) {
      console.error(error);
      setFeedbackError('An error occurred while submitting feedback.');
    }
  };

  if (!open) return null;

  return (
    <ModalBackdrop onClick={onClose}>
      {/* Prevent clicks inside the modal card from closing the modal */}
      <ModalCard onClick={(e) => e.stopPropagation()}>
        {isSubmitted ? (
          <>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Thank you for your feedback!
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button variant="contained" onClick={onClose} color="primary">
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Report a Bug / Submit Feedback
            </Typography>
            <CustomTextArea
              label="Your Feedback"
              placeholder="Enter your feedback here..."
              value={feedback}
              onChange={(e) => {
                setFeedback(e.target.value);
                if (e.target.value.trim()) setFeedbackError('');
              }}
              error={Boolean(feedbackError)}
              helperText={feedbackError}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit} color="primary">
                Submit
              </Button>
            </Box>
          </>
        )}
      </ModalCard>
    </ModalBackdrop>
  );
}

export default FeedbackModal;
